import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-brain-circuit',
    title: '1. Generative AI & Custom LLMs',
    desc: 'Develop, fine-tune, and deploy custom Large Language Models (LLMs) and retrieval-augmented generation (RAG) pipelines tailored to secure enterprise knowledge stores.',
  },
  {
    icon: 'fa-solid fa-chart-line-up',
    title: '2. Predictive Analytics & Forecasting',
    desc: 'Build custom machine learning models to forecast operational risks, dynamic demand patterns, financial fluctuations, and customer churn probabilities.',
  },
  {
    icon: 'fa-solid fa-eye',
    title: '3. Computer Vision & Visual AI',
    desc: 'Implement real-time image and video processing for quality inspection, object recognition, automated spatial tracking, and facial recognition.',
  },
  {
    icon: 'fa-solid fa-comments',
    title: '4. Natural Language Processing (NLP)',
    desc: 'Automate content extraction, sentiment analysis, multi-language translation, and intelligent document classification from unstructured text.',
  },
  {
    icon: 'fa-solid fa-gears',
    title: '5. MLOps & Continuous Model Delivery',
    desc: 'Establish scalable MLOps pipelines for automated model retraining, version control, drift monitoring, and seamless production deployments.',
  },
  {
    icon: 'fa-solid fa-robot',
    title: '6. AI Autonomous Agents',
    desc: 'Design and deploy multi-agent systems that autonomously execute multi-step business workflows, decision trees, and complex API integrations.',
  },
  {
    icon: 'fa-solid fa-shield-cat',
    title: '7. Responsible & Ethical AI Governance',
    desc: 'Implement strict bias detection, model explainability frameworks, data privacy barriers, and regulatory compliance protocols across all AI systems.',
  },
  {
    icon: 'fa-solid fa-wand-magic-sparkles',
    title: '8. Intelligent Process Automation (IPA)',
    desc: 'Combine Robotic Process Automation (RPA) with deep learning to handle non-standardized documents, voice commands, and complex operational tasks.',
  },
  {
    icon: 'fa-solid fa-user-gear',
    title: '9. Recommendation & Personalization Engines',
    desc: 'Deliver hyper-personalized product recommendations, dynamic content feeds, and contextual search results driven by real-time behavioral data.',
  },
  {
    icon: 'fa-solid fa-microchip',
    title: '10. Edge AI & On-Device Inference',
    desc: 'Optimize and deploy ultra-lightweight deep learning models onto edge hardware, IoT gateways, and mobile devices for low-latency offline execution.',
  },
];

const whyChooseUs = [
  'Enterprise RAG & fine-tuned LLM architectures',
  'Automated MLOps pipelines with zero-downtime deployment',
  'Strict AI governance, explainability, and data security',
  'Multi-modal computer vision and NLP processing capabilities',
  'Proven reduction in manual operational process friction',
  'Scalable model training across hybrid and multi-cloud environments',
  'Real-time model performance, drift, and accuracy monitoring',
  'Seamless integration with modern APIs, databases, and microservices',
  'Domain-specific dataset curation and custom annotation workflows',
  'End-to-end guidance from proof-of-concept (PoC) to full deployment',
];

const processSteps = [
  { step: '01', title: 'Data & Use Case Assessment', desc: 'Identify high-value business challenges, evaluate data quality, and define measurable AI performance targets.' },
  { step: '02', title: 'Data Prep & Feature Engineering', desc: 'Cleanse, label, structure, and augment training datasets while building scalable feature stores.' },
  { step: '03', title: 'Model Architecture & Training', desc: 'Select, fine-tune, and train state-of-the-art algorithms optimized for accuracy and computational efficiency.' },
  { step: '04', title: 'Validation & Bias Auditing', desc: 'Perform rigorous stress testing, explainability checks, and bias assessments to validate model behavior.' },
  { step: '05', title: 'MLOps Pipeline Integration', desc: 'Deploy models into containerized cloud infrastructure with CI/CD automation and monitoring.' },
  { step: '06', title: 'Continuous Learning & Tuning', desc: 'Track real-world performance, manage model drift, and retrain using dynamic feedback loops.' },
];

const techStack = [
  { category: 'AI & ML Frameworks', items: ['PyTorch', 'TensorFlow', 'Scikit-Learn', 'Keras', 'Hugging Face'] },
  { category: 'Generative AI & LLMs', items: ['OpenAI API', 'LangChain', 'LlamaIndex', 'vLLM', 'Pinecone'] },
  { category: 'MLOps & Infrastructure', items: ['MLflow', 'Kubeflow', 'Databricks', 'AWS SageMaker', 'Azure ML'] },
  { category: 'Data Engineering & Vision', items: ['OpenCV', 'Pandas', 'NumPy', 'Dask', 'Apache Spark'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const AIAndML = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Digital Transformation
            </span>
            <h1 className="display-4 fw-bold mb-3">
              AI & <span className="gradient-text-cyan">Machine Learning</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we turn cutting-edge artificial intelligence into production-ready business solutions. From custom Large Language Models to automated MLOps pipelines, we empower your business to innovate, automate, and scale.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Accelerate Your AI Strategy &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              AI & Machine Learning Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">AI & Machine Learning Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">AI Engineering Methodology</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Deploy Intelligent AI Systems with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Transform your proprietary enterprise data into predictive insights, process automation, and adaptive user experiences.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with an AI Architect &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIAndML;